import { useEffect, useState } from "react";
import { getEnrichmentForPlaces } from "../services/enrichmentService";
import { getSourceAttribute } from "../utils/placeAttributes";
import { maintenanceStatus } from "../utils/placeMaintenance";

const FIELDS = [
  [
    "difficulty",
    "Difficulty",
    { easy: "Easy", moderate: "Moderate", hard: "Hard" },
  ],
  [
    "parking",
    "Parking",
    {
      yes: "Parking listed",
      limited: "Limited parking",
      no: "No parking listed",
    },
  ],
  [
    "restrooms",
    "Restrooms",
    {
      yes: "Restrooms listed",
      seasonal: "Seasonal toilets",
      no: "No restrooms listed",
    },
  ],
  [
    "surface",
    "Trail surface",
    {
      paved: "Paved",
      gravel: "Gravel",
      natural: "Natural",
      mixed: "Mixed surfaces",
    },
  ],
  [
    "dog_friendly",
    "Dogs",
    {
      yes: "Listed as allowed",
      leashed: "Leash required",
      no: "Listed as not allowed",
    },
  ],
  [
    "permit_or_fee",
    "Entry / permits",
    {
      none: "No entry fee listed",
      fee: "Entry fee listed",
      permit: "Permit listed",
      license: "License required",
    },
  ],
  [
    "fishing",
    "Fishing",
    { yes: "Fishing mentioned in source", no: "Listed as not allowed" },
  ],
  [
    "residents_only",
    "Access",
    { yes: "Residents only", no: "Not restricted to residents" },
  ],
];

function sourceUrl(value) {
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url : null;
  } catch {
    return null;
  }
}

export default function PlaceNotes({ place, activityId }) {
  const [result, setResult] = useState(null);
  useEffect(() => {
    let active = true;
    setResult(null);
    getEnrichmentForPlaces([place])
      .then((records) => {
        if (active) setResult({ record: records.get(place.id) });
      })
      .catch(() => {
        if (active) setResult({ record: null });
      });
    return () => {
      active = false;
    };
  }, [place.id, place.lat, place.lon]);

  if (!result)
    return (
      <p role="status" className="my-4 text-sm text-stone-500">
        Loading place notes...
      </p>
    );
  return (
    <PlaceNotesContent
      record={result.record}
      place={place}
      activityId={activityId}
    />
  );
}

export function PlaceNotesContent({ record, place, activityId }) {
  const needsRecheck = Object.values(record?.attributes || {}).some(
    (attribute) =>
      ["stale", "needs-review"].includes(maintenanceStatus(attribute)),
  );
  const fishing = activityId === "fishing" || place.type === "fishing";
  const fields = FIELDS.filter(([key]) =>
    key === "fishing" ? fishing : key === "difficulty" ? !fishing : true,
  );
  return (
    <section className="mb-5 mt-4" aria-label="Know before you go">
      <h4 className="text-base font-semibold">Know before you go</h4>
      {record?.summary && !needsRecheck && (
        <p className="mt-2 text-sm leading-relaxed text-stone-600">
          {record.summary}
        </p>
      )}
      <p className="mt-2 text-xs leading-relaxed text-stone-500">
        Dates show when a source was checked, not an on-site inspection or a
        guarantee of access today. Missing or outdated details are marked below.
      </p>
      {record?.attributes?.access_status?.maintenanceStatus ===
        "needs-review" && (
        <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          A possible access restriction needs review. Confirm with the land
          manager before going.
        </p>
      )}
      <dl className="mt-3 divide-y divide-stone-200 rounded-xl border border-stone-200 px-3">
        {fields.map(([key, label, values]) => {
          const attribute = getSourceAttribute(record, key);
          const value = values[String(attribute?.value ?? "").toLowerCase()];
          const source = sourceUrl(attribute?.source);
          const known = value && attribute?.confidence !== "low" && source;
          const status = maintenanceStatus(record?.attributes?.[key]);
          return (
            <div key={key} className="py-3">
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-sm font-medium">{label}</dt>
                <dd
                  className={`text-right text-sm ${known ? "text-stone-700" : "text-stone-500"}`}
                >
                  {known
                    ? value
                    : ["stale", "needs-review"].includes(status)
                      ? "Needs rechecking"
                      : "Not confirmed"}
                </dd>
              </div>
              {known && status === "checked" && (
                <p className="mt-1 text-right text-xs text-stone-500">
                  Source checked{" "}
                  {new Date(attribute.checkedAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              )}
              {known && (
                <details className="mt-1.5 text-xs leading-relaxed text-stone-600">
                  <summary className="cursor-pointer underline underline-offset-2">
                    Read source note
                  </summary>
                  {attribute.evidence && (
                    <p className="mt-2">&ldquo;{attribute.evidence}&rdquo;</p>
                  )}
                  {attribute.note && <p className="mt-1">{attribute.note}</p>}
                  <a
                    href={source.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block break-all font-medium text-[#45633f] underline"
                  >
                    {source.hostname.replace(/^www\./, "")}
                  </a>
                </details>
              )}
            </div>
          );
        })}
      </dl>
      {(activityId === "fishing" || place.type === "fishing") && (
        <p className="mt-3 text-xs leading-relaxed text-stone-600">
          An entry fee note does not establish fishing-license requirements or
          permission to fish. Check current rules for the specific water.
        </p>
      )}
    </section>
  );
}
